import { useEffect, useRef, useState } from 'preact/hooks';
import { APP_NAME, APP_NAME_AR } from '../data/brand';
import { imageUrl } from '../data/gallery';
import { keepHonorifics, quranText } from '../data/islam';
import { SIZE_BODIES, type SizeBody, type SizePhoto } from '../data/sizes';
import { NEWS_ACTION, RELEASES, type NewsTarget, type StoryPage } from '../data/whatsNew';
import { panel, storyShown } from '../state';
import { useDialog } from './chrome';
import { openGallery } from './galleryNav';
import { SayQuran } from './SayArabic';
import { Icon } from './icons';
import { openSizes } from './sizeBoards';

// An update told in pages, in place of the short card: a picture, a heading, a few lines and something to try on
// each, swiped or stepped through with Next and Back. Shown once after an update that has a story (App), and again
// from the list of updates. Every word is in src/data/whatsNew.ts.

/** Open the part of the app a page's button names. */
function openTarget(t: NewsTarget) {
  if (t === 'gallery') openGallery();
  else if (t === 'sizes') openSizes();
  else panel.value = t;
}

const credits = (p: StoryPage) => [...new Set((p.photos ?? []).map((ph) => ph.credit))];
/** A photograph alone, or behind the name, fills the width, so it is drawn from the full picture to stay sharp; several
 *  share the width, as squares. */
const photoSize = (p: StoryPage) => (p.art === 'name' || p.photos?.length === 1 ? 2048 : 480);

/** The share of the picture's width the first (largest) world takes, and the column the smaller ones share. */
const BIG = 64;
const SMALL = 22;

/** Worlds from "How big are they?" at their true sizes: the first fills most of the width, the others stand in a
 * column beside it, each named, drawn to the same scale. */
function Sizes({ ids }: { ids: string[] }) {
  const bodies = ids.map((id) => SIZE_BODIES.find((b) => b.id === id)).filter((b): b is SizeBody & { photo: SizePhoto } => !!b?.photo);
  if (!bodies.length) return null;
  const [big, ...rest] = bodies;
  const img = (b: SizeBody & { photo: SizePhoto }) => (
    <img src={`${import.meta.env.BASE_URL}${b.photo.file}?v=${b.photo.v}`} alt="" width={b.photo.w} height={b.photo.h} decoding="async" draggable={false} />
  );
  const credit = [...new Set(bodies.map((b) => b.photo.credit))];
  return (
    <div class="upd-art">
      <div class="upd-worlds" role="img" aria-label={`${bodies.map((b) => b.name).join(', ')}, at their true sizes`}>
        <figure class="upd-world" style={{ width: `${BIG}%` }}>{img(big)}</figure>
        <div class="upd-small" style={{ width: `${SMALL}%` }}>
          {rest.map((b) => (
            <figure key={b.id} class="upd-world" style={{ width: `${(b.photo.wKm / big.photo.wKm) * (BIG / SMALL) * 100}%` }}>
              {img(b)}
              <figcaption>{b.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
      <p class="upd-credit">Photos: {credit.join(' · ')}</p>
    </div>
  );
}

/** The bar along the bottom as 8 October's update left it: four buttons (More came on 23 October 2026). */
function Dock() {
  const item = (icon: string, label: string) => (
    <span class="dock-btn"><Icon name={icon} size={24} /><span>{label}</span></span>
  );
  return (
    <div class="upd-art">
      <div class="upd-dockart" aria-hidden="true">
        <div class="upd-dock">
          {item('search', 'Search')}
          {item('tonight', 'Tonight')}
          <span class="dock-main"><span class="disc"><Icon name="phone" size={20} /></span><span>Point</span></span>
          {item('layers', 'Sky view')}
        </div>
      </div>
    </div>
  );
}

/** `here`: the page is the one showing; a verse's recitation is offered only there, so it stops when the page is left. */
function Art({ page, here }: { page: StoryPage; here: boolean }) {
  const photos = page.photos ?? [];
  if (page.art === 'sizes') return <Sizes ids={page.sizes ?? []} />;
  if (page.art === 'dock') return <Dock />;
  if (page.art === 'icon') {
    return (
      <div class="upd-art">
        <div class="upd-dockart">
          <img class="upd-icon" src={`${import.meta.env.BASE_URL}icons/icon.svg`} width={120} height={120} decoding="async" draggable={false}
            alt={`${APP_NAME}’s icon: a thin crescent Moon over the horizon at dusk, with stars`} />
        </div>
      </div>
    );
  }
  if (page.art === 'verse' && page.quran) {
    return (
      <div class="upd-art">
        <div class="upd-verse">
          <p class="quran-text" lang="ar" dir="rtl">{quranText(page.quran.arabic)}</p>
          <p class="upd-verse-ref" lang="ar" dir="rtl">{page.quran.refAr}</p>
          {here && <SayQuran text={page.quran.arabic} />}
        </div>
      </div>
    );
  }
  if (page.art === 'name') {
    const ph = photos[0];
    return (
      <div class="upd-art upd-name">
        {ph && <img class="upd-hero" src={imageUrl(ph.id, 2048)} alt={ph.alt} decoding="async" draggable={false} />}
        <p class="brand upd-brand"><span>{APP_NAME}</span><span class="arabic-script" lang="ar" dir="rtl">{APP_NAME_AR}</span></p>
        <p class="upd-credit">Photo: {credits(page).join(' · ')}</p>
      </div>
    );
  }
  return (
    <div class="upd-art">
      <div class={`upd-photos n${photos.length}`}>
        {photos.map((ph) => (
          <img key={ph.id} src={imageUrl(ph.id, photoSize(page))} alt={ph.alt} decoding="async" draggable={false}
            onLoad={(e) => (e.currentTarget as HTMLElement).classList.add('in')} />
        ))}
      </div>
      <p class="upd-credit">{photos.length === 1 ? 'Photo' : 'Photos'}: {credits(page).join(' · ')}</p>
    </div>
  );
}

export function UpdateStory({ id, onDone }: { id: string; onDone: () => void }) {
  const pages = RELEASES.find((r) => r.id === id)?.story ?? [];
  const [at, setAt] = useState(0);
  const [dx, setDx] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const titles = useRef<(HTMLHeadingElement | null)[]>([]);
  const drag = useRef<{ x: number; y: number; t: number; on: boolean; id: number } | null>(null);
  useDialog(root, true);
  // On screen, for feedback's list of what was looked at (src/ui/trail.ts).
  useEffect(() => {
    storyShown.value = id;
    return () => {
      if (storyShown.peek() === id) storyShown.value = null;
    };
  }, [id]);
  const last = at === pages.length - 1;
  const go = (d: number) => setAt((i) => Math.max(0, Math.min(pages.length - 1, i + d)));
  const leave = (then?: () => void) => {
    onDone();
    then?.();
  };
  // Each page starts at its heading, so a screen reader reads it as it comes.
  useEffect(() => titles.current[at]?.focus({ preventScroll: true }), [at]);
  // Every picture is asked for at once, so none appears late as the pages turn.
  useEffect(() => {
    for (const p of pages) for (const ph of p.photos ?? []) new Image().src = imageUrl(ph.id, photoSize(p));
  }, [id]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') leave();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else return;
      e.preventDefault();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [pages.length]);
  if (!pages.length) return null;

  // A sideways swipe turns the page; an up-and-down one scrolls the words.
  const down = (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    drag.current = { x: e.clientX, y: e.clientY, t: performance.now(), on: false, id: e.pointerId };
  };
  const move = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const mx = e.clientX - d.x;
    if (!d.on) {
      if (Math.abs(mx) < 8 || Math.abs(mx) < Math.abs(e.clientY - d.y)) return;
      d.on = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    // Past either end the page gives a little, then holds.
    const edge = (at === 0 && mx > 0) || (last && mx < 0);
    setDx(edge ? mx / 3 : mx);
  };
  // A touch the browser takes back (to scroll, or to drag an image) says nothing of where the finger went (Chrome
  // reports x = 0), so it never turns the page: the page just settles back.
  const cancel = () => {
    drag.current = null;
    setDx(0);
  };
  const up = (e: PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d?.on) return;
    const mx = e.clientX - d.x;
    const fast = Math.abs(mx) / Math.max(1, performance.now() - d.t) > 0.45;
    const w = (e.currentTarget as HTMLElement).clientWidth;
    if (mx < 0 && (mx < -w * 0.2 || fast)) go(1);
    else if (mx > 0 && (mx > w * 0.2 || fast)) go(-1);
    setDx(0);
  };

  return (
    <div ref={root} class="upd" role="dialog" aria-modal="true" aria-label={`What’s new in ${APP_NAME}`}>
      <div class="upd-top">
        <div class="upd-progress" aria-hidden="true">
          {pages.map((_, i) => <span key={i} class={i <= at ? 'on' : ''} />)}
        </div>
        <button class="upd-close" aria-label="Close" onClick={() => leave()}><Icon name="close" size={18} /></button>
      </div>
      <div class={`upd-track${dx ? ' dragging' : ''}`} style={{ transform: `translateX(calc(${-at * 100}% + ${dx}px))` }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}>
        {pages.map((p, i) => (
          <section key={i} class={`upd-page art-${p.art}${p.points ? ' points' : ''}`} aria-hidden={i !== at} inert={i !== at}>
            <Art page={p} here={i === at} />
            <div class="upd-words">
              <h2 tabIndex={-1} ref={(el) => void (titles.current[i] = el)}>
                <span class="sr-only">{i + 1} of {pages.length}: </span>{p.title}
              </h2>
              <p>{keepHonorifics(p.text)}</p>
              {p.points && (
                <ul class="upd-points">
                  {p.points.map((pt) => (
                    <li key={pt.text}><span class="news-icon"><Icon name={pt.icon} size={18} /></span><span>{keepHonorifics(pt.text)}</span></li>
                  ))}
                </ul>
              )}
              {p.show && (
                <button class="btn-ghost upd-try" onClick={() => leave(() => openTarget(p.show!))}>
                  {NEWS_ACTION[p.show]} <Icon name="chevron" size={15} />
                </button>
              )}
            </div>
          </section>
        ))}
      </div>
      <div class="upd-nav">
        {at > 0 && <button class="btn-ghost" onClick={() => go(-1)}>Back</button>}
        <button class="btn" onClick={() => (last ? leave() : go(1))}>{last ? 'Done' : 'Next'}</button>
      </div>
      <p class="news-where upd-where"><Icon name="gear" size={14} /> Find this again in Settings, under What’s new</p>
    </div>
  );
}
